import { Glass } from "@coach/ui";
import { useState } from "react";
import { useNavigate } from "react-router";
import {
  markConfirmedOrg,
  switchActiveOrganization,
} from "../lib/active-selection.js";
import { useActiveOrgId, useMyOrganizations } from "../lib/use-active-org.js";

/**
 * `/select/org` — full-screen Verein-Auswahl shown after a fresh login
 * (or via the "Verein wechseln" button in the account menu). Mirrors the
 * landing's auth-shell visuals so the transition from sign-in to picking
 * a club feels like one continuous flow.
 *
 * Single-org coaches never see this page — the gate routes them straight
 * past it. We still render a graceful "nothing to choose" state in case
 * someone deep-links here.
 */
export function SelectOrgPage() {
  const orgs = useMyOrganizations();
  const activeOrgId = useActiveOrgId();
  const navigate = useNavigate();
  const [busyId, setBusyId] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function pick(orgId: string) {
    setBusyId(orgId);
    setErr(null);
    try {
      if (orgId === activeOrgId) {
        // Already active — no need to roundtrip; just stamp the
        // confirmation and continue to /select/team (or /).
        markConfirmedOrg(orgId);
        navigate("/select/team", { replace: true });
        return;
      }
      // Triggers org-active POST, license re-issue, /me wipe, hard
      // reload. The post-reload gate will then surface /select/team if
      // there's still a choice to make in the new org.
      await switchActiveOrganization(orgId);
    } catch (e) {
      setErr((e as Error).message ?? "Fehler beim Wechsel");
      setBusyId(null);
    }
  }

  return (
    <div className="grid min-h-svh place-items-center bg-background p-6">
      <Glass
        level="floating"
        padding="lg"
        className="flex w-full max-w-md flex-col gap-5"
      >
        <header className="text-center">
          <span className="text-label-caps uppercase tracking-widest text-pitch-lime">
            Coach Organizer
          </span>
          <h1 className="text-display-sm font-semibold">Verein auswählen</h1>
          <p className="mt-2 text-body-sm text-muted-foreground">
            Du gehörst zu mehreren Vereinen. Wähle aus, mit welchem du auf
            diesem Gerät arbeiten möchtest — Wechsel ist jederzeit über den
            Account-Menüpunkt „Verein wechseln" möglich.
          </p>
        </header>

        {orgs.length === 0 ? (
          <p className="text-center text-body-sm text-muted-foreground">
            Noch kein Verein. Lege im Dashboard einen an, dann erscheint er
            hier.
          </p>
        ) : (
          <ul className="flex flex-col gap-2">
            {orgs.map((o) => {
              const isActive = o.id === activeOrgId;
              return (
                <li key={o.id}>
                  <button
                    type="button"
                    disabled={busyId !== null}
                    onClick={() => pick(o.id)}
                    className="flex w-full items-center justify-between gap-3 rounded-md border border-white/10 bg-white/2 p-3 text-left hover:border-pitch-lime/40 disabled:opacity-60"
                  >
                    <div className="flex min-w-0 flex-col">
                      <span className="truncate text-body-md font-medium">
                        {o.name}
                      </span>
                      <span className="text-body-sm text-muted-foreground">
                        {o.role === "owner" ? "Inhaber" : "Coach"}
                        {isActive ? " · aktuell aktiv" : ""}
                      </span>
                    </div>
                    {o.logo ? (
                      <img
                        src={o.logo}
                        alt=""
                        className="h-10 w-10 rounded-md object-cover"
                      />
                    ) : (
                      <span className="grid h-10 w-10 place-items-center rounded-md bg-white/5 text-label-caps uppercase text-muted-foreground">
                        {o.name.slice(0, 2)}
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        {err && (
          <p className="text-body-sm text-tactical-red" role="alert">
            {err}
          </p>
        )}
      </Glass>
    </div>
  );
}
